import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Box, Typography } from '@mui/material'

import { PSDefaultTemplate, PSTemplateRequest } from 'types/template'

import {
  useDeleteOrganizationTemplateMutation,
  useEditOrganizationTemplateMutation,
} from 'services/organizations'
import { useDeleteTemplateMutation, useEditTemplateMutation } from 'services/templates'

import { AppRoute, getPaymentIconByCode } from 'assets/const'

import ConfirmModal from 'components/ConfirmModal'
import ScrollableBox from 'components/ScrollableBox'

import { toastError } from '../../../utils/toast'
import EditDetails from '../EditDetails'
import TemplateProfileCard from '../TemplateProfileCard'

interface Props {
  savedTemplates: PSTemplateRequest[]
  defaultTemplates: PSDefaultTemplate[]
  isSavedUpdating: boolean
  setIsSavedUpdating: (isSavedUpdating: boolean) => void
  isModalOpen: boolean
  setIsModalOpen: (isModalOpen: boolean) => void
  organizationId?: string
}

const SavedTemplate = ({
  savedTemplates,
  defaultTemplates,
  isSavedUpdating,
  isModalOpen,
  setIsModalOpen,
  organizationId,
  setIsSavedUpdating,
}: Props) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const templateId = searchParams.get('templateId')
  const [activeTemplate, setActiveTemplate] = useState<PSDefaultTemplate | null>(null)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  const [editTemplate, { isLoading: isEditing }] = useEditTemplateMutation()
  const [editOrgTemplate, { isLoading: isOrgEditing }] = useEditOrganizationTemplateMutation()

  const [deleteTemplate, { isLoading: isDeleting, originalArgs }] = useDeleteTemplateMutation()
  const [deleteOrgTemplate, { isLoading: isOrgDeleting, originalArgs: originalOrgArgs }] =
    useDeleteOrganizationTemplateMutation()

  const handleEdit = ({ id, code, name, ...values }: PSTemplateRequest) => {
    const foundTemplate = defaultTemplates.find((t) => t.code === code)
    if (!foundTemplate) {
      toastError('Template not found')
      return
    }
    const template: PSDefaultTemplate = {
      id,
      name,
      code,
      psCode: foundTemplate.psCode,
      paymentFormDefinition: foundTemplate.paymentFormDefinition,
      values: values?.paymentFormDefinition,
    }
    setActiveTemplate(template)
    setIsModalOpen(true)
  }

  const navigateToProfileTemplates = () => {
    navigate(`/${AppRoute.Profile}/${AppRoute.Templates}`)
  }

  const handleDelete = (id: string) => {
    setSearchParams((prevParams) => {
      const params = new URLSearchParams(prevParams)
      params.set('templateId', id)
      return params
    })
    setIsConfirmModalOpen(true)
  }

  const confirmDelete = () => {
    if (templateId) {
      organizationId
        ? deleteOrgTemplate({ organizationId, templateId })
            .unwrap()
            .then(() => {
              setIsConfirmModalOpen(false)
              setIsSavedUpdating(true)
            })
        : deleteTemplate({ templateId })
            .unwrap()
            .then(() => {
              navigateToProfileTemplates()
              setIsConfirmModalOpen(false)
              setIsSavedUpdating(true)
            })
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleSaveTemplate = (template: PSTemplateRequest) => {
    setIsSavedUpdating(true)
    organizationId
      ? editOrgTemplate({ organizationId, template })
          .unwrap()
          .then(() => {
            handleModalClose()
            setIsSavedUpdating(true)
          })
          .catch(() => null)
      : editTemplate(template)
          .unwrap()
          .then(() => {
            navigateToProfileTemplates()
            handleModalClose()
            setIsSavedUpdating(true)
          })
          .catch(() => null)
  }

  return (
    <>
      <Box mt={4}>
        <Typography sx={{ mb: 3, fontWeight: 500 }}>Manage saved template:</Typography>
        <ScrollableBox maxHeight={220} width="100%">
          {savedTemplates.map((template, index) => (
            <TemplateProfileCard
              handleDelete={handleDelete}
              handleEdit={handleEdit}
              isDeleting={
                isDeleting &&
                (originalArgs?.templateId === template.id ||
                  originalOrgArgs?.templateId === template.id)
              }
              template={template}
              key={template.id}
              icon={getPaymentIconByCode[template.code as keyof typeof getPaymentIconByCode]}
              index={index}
            />
          ))}
        </ScrollableBox>
      </Box>
      {isModalOpen && activeTemplate && (
        <EditDetails
          template={activeTemplate}
          isFetching={isSavedUpdating || isEditing || isOrgEditing}
          handleClose={handleModalClose}
          handleSave={handleSaveTemplate}
        />
      )}
      {isConfirmModalOpen && (
        <ConfirmModal
          variant="confirm"
          onConfirm={confirmDelete}
          loading={isDeleting || isOrgDeleting}
          onClose={() => {
            setIsConfirmModalOpen(false)
            !organizationId && navigateToProfileTemplates()
          }}
          text="Are you sure you want to delete the withdraw template?"
        />
      )}
    </>
  )
}

export default SavedTemplate
